// src/pages/offlineSales/terminal/components/ReceiptModal.jsx
import React, { useState } from 'react';
import { X, Printer, MessageCircle, Smartphone, CheckCircle } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { useAuth } from '../../../../hooks/useAuth';
import { sendInvoiceSms } from '../../../../services/api/offlineSales/orders';
import { getImageUrl } from '../../../../utils/imageUtils';
import { buildThermalReceiptCss, printThermalReceipt, shortCashierName } from '../../../../utils/thermalReceiptPrint';
import styles from '../OfflineSalesTerminal.module.css';

const ReceiptModal = ({ order, shop, settings, smsEnabled, onClose, onNewSale }) => {
  const { t, formatCurrency } = useLocalization();
  const { user } = useAuth();
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsError, setSmsError] = useState(null);

  if (!order) return null;

  // Print only the receipt on 80mm paper (shared with the order reprint)
  const handlePrint = () => {
    printThermalReceipt(document.getElementById('pos-receipt'), {
      title: `Receipt ${order.orderNumber || ''}`,
      css: buildThermalReceiptCss(styles),
    });
  };

  // Can share only when customer is not cash sales, has phone, and order has a share token
  const canShare = order.customer && !order.customer.isCashSales && order.customer.phone && order.shareToken;

  const handleWhatsApp = () => {
    const invoiceUrl = `${window.location.origin}/invoice/${order.shareToken}`;
    const shopName = shop?.name || 'Store';
    const message = encodeURIComponent(`Your invoice from ${shopName}: ${invoiceUrl}`);
    let phone = order.customer.phone.trim();
    if (phone.startsWith('0')) phone = '94' + phone.slice(1);
    else if (phone.startsWith('+')) phone = phone.slice(1);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  const handleSendSms = async () => {
    setSmsSending(true);
    setSmsError(null);
    try {
      await sendInvoiceSms(order.id);
      setSmsSent(true);
    } catch (err) {
      setSmsError(err?.response?.data?.message || 'Failed to send SMS');
    } finally {
      setSmsSending(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.receiptModal}`}>
        <div className={styles.modalHeader}>
          <h3>{t('pos.receipt')}</h3>
          <button onClick={onClose} className={styles.modalClose}><X size={18} /></button>
        </div>

        {/* Printable receipt area */}
        <div className={styles.receiptArea} id="pos-receipt">
          {shop?.logo && (
            <div className={styles.receiptLogoWrap}>
              <img
                src={new URL(getImageUrl(shop.logo), window.location.href).href}
                alt={shop.name}
                className={styles.receiptLogo}
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          )}
          <p className={styles.receiptShop}>{shop?.name}</p>

          {settings?.receiptHeader && (
            <div className={styles.receiptHeader}>{settings.receiptHeader}</div>
          )}

          <div className={styles.receiptMeta}>
            <div className={styles.receiptMetaRow}>
              <span>{t('pos.orderNumber')}</span>{' '}
              <span>{order.orderNumber}</span>
            </div>
            <div className={styles.receiptMetaRow}>
              <span>{t('pos.date')}</span>{' '}
              <span>{new Date(order.createdAt).toLocaleString()}</span>
            </div>
            <div className={styles.receiptMetaRow}>
              <span>{t('pos.cashier')}</span>{' '}
              <span>{shortCashierName(user?.name)}</span>
            </div>
            <div className={styles.receiptMetaRow}>
              <span>{t('pos.customer')}</span>{' '}
              <span>{order.customer?.name || t('pos.cashSales')}</span>
            </div>
          </div>

          <hr className={styles.receiptDivider} />

          <div className={styles.receiptItems}>
            {(order.orderItems || []).map((item, idx) => (
              <div key={idx} className={styles.receiptItem}>
                <span className={styles.receiptItemName}>{item.displayName}</span>
                <span className={styles.receiptItemQty}>×{item.quantity}</span>
                <span className={styles.receiptItemTotal}>{formatCurrency(item.lineTotal)}</span>
              </div>
            ))}
          </div>

          <hr className={styles.receiptDivider} />

          <div className={styles.receiptTotals}>
            {parseFloat(order.totalDiscount) > 0 && (
              <div className={styles.receiptTotalRow}>
                <span>{t('pos.itemDiscount')}</span>
                <span>-{formatCurrency(order.totalDiscount)}</span>
              </div>
            )}
            {parseFloat(order.orderDiscount) > 0 && (
              <div className={styles.receiptTotalRow}>
                <span>{t('pos.orderDiscount')}</span>
                <span>-{formatCurrency(order.orderDiscount)}</span>
              </div>
            )}
            {parseFloat(order.loyaltyDiscountAmount) > 0 && (
              <div className={styles.receiptTotalRow}>
                <span>{t('pos.loyaltyDiscount')}</span>
                <span>-{formatCurrency(order.loyaltyDiscountAmount)}</span>
              </div>
            )}
            <div className={`${styles.receiptTotalRow} ${styles.receiptGrandTotal}`}>
              <strong>{t('pos.grandTotal')}</strong>
              <strong>{formatCurrency(order.grandTotal)}</strong>
            </div>
          </div>

          <hr className={styles.receiptDivider} />

          <div className={styles.receiptPayments}>
            <div className={styles.receiptPaymentHeader}>{t('pos.payments')}</div>
            {(order.paymentRecords || []).map((p, idx) => (
              <div key={idx} className={styles.receiptTotalRow}>
                <span>{t(`pos.${p.PaymentMethod?.key}`, p.PaymentMethod?.label || p.PaymentMethod?.key)}</span>
                <span>{formatCurrency(p.paymentAmount)}</span>
              </div>
            ))}
            <div className={styles.receiptTotalRow}>
              <span>{t('pos.totalPaid')}</span>
              <strong>{formatCurrency(order.paidAmount)}</strong>
            </div>
            {parseFloat(order.changeAmount) > 0 && (
              <div className={styles.receiptTotalRow}>
                <span>{t('pos.change')}</span>
                <strong>{formatCurrency(order.changeAmount)}</strong>
              </div>
            )}
            {parseFloat(order.balanceAmount) > 0 && (
              <div className={styles.receiptTotalRow}>
                <span>{t('pos.balanceDue')}</span>
                <strong style={{ color: 'var(--color-error)' }}>{formatCurrency(order.balanceAmount)}</strong>
              </div>
            )}
          </div>

          {parseFloat(order.loyaltyPointsEarned) > 0 && (
            <div className={styles.receiptLoyalty}>
              +{order.loyaltyPointsEarned} {t('pos.loyaltyPointsLabel')} {t('common.earned', 'earned')}
            </div>
          )}

          {settings?.receiptFooter && (
            <div className={styles.receiptFooter}>{settings.receiptFooter}</div>
          )}
        </div>

        {/* Share section — outside print area */}
        {canShare && (
          <div className={styles.receiptShareSection}>
            <span className={styles.receiptShareLabel}>{t('pos.shareInvoice', 'Share Invoice')}</span>
            <div className={styles.receiptShareBtns}>
              <button
                onClick={handleWhatsApp}
                className={`${styles.receiptShareBtn} ${styles.receiptShareBtnWa}`}
              >
                <MessageCircle size={15} />
                {t('pos.sendViaWhatsApp', 'WhatsApp')}
              </button>

              {smsEnabled && (
                smsSent ? (
                  <span className={styles.receiptSmsSent}>
                    <CheckCircle size={14} /> {t('pos.smsSent', 'SMS Sent')}
                  </span>
                ) : (
                  <button
                    onClick={handleSendSms}
                    disabled={smsSending}
                    className={`${styles.receiptShareBtn} ${styles.receiptShareBtnSms}`}
                  >
                    <Smartphone size={15} />
                    {smsSending ? t('pos.sendingInvoice', 'Sending…') : t('pos.sendViaSms', 'Send SMS')}
                  </button>
                )
              )}
            </div>
            {smsError && <span className={styles.receiptSmsError}>{smsError}</span>}
          </div>
        )}

        <div className={styles.modalFooter}>
          <button onClick={handlePrint} className={styles.actionBtnSecondary}>
            <Printer size={16} /> {t('pos.printReceipt')}
          </button>
          <button onClick={onNewSale} className={styles.chargeBtn}>
            {t('pos.newSale')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
